import { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { ref, push, get } from 'firebase/database';
import { Spinner } from '../ui/Spinner';
import { useBranches } from '../../hooks/useBranches';
import { resolveBranchRef } from '../../utils/branchResolver';

const CreatePaymentRequestModal = ({ isOpen, onClose, onSuccess }) => {
  const { byId: branchesById, bySlug: branchesBySlug } = useBranches();
  const [loading, setLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [batches, setBatches] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [studentSearch, setStudentSearch] = useState('');

  const [formData, setFormData] = useState({
    courseId: '',
    batchId: '',
    selectedStudents: [],
    paymentType: '',
    amount: '',
    installmentNumber: '',
    totalInstallments: '',
    dueDate: new Date().toISOString().split('T')[0],
    notes: '',
    status: 'pending',
  });

  const [selectedStudents, setSelectedStudents] = useState([]);

  // Fetch all students and courses on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        // Fetch courses
        const coursesSnapshot = await get(ref(db, 'courses'));
        const coursesData = coursesSnapshot.val() || {};
        setCourses(
          Object.entries(coursesData).map(([id, data]) => ({
            id,
            name: data.name,
            ...data,
          })),
        );

        // Fetch all students
        const studentsSnapshot = await get(ref(db, 'students'));
        const studentsData = studentsSnapshot.val() || {};
        const studentsArray = Object.entries(studentsData).map(
          ([id, data]) => ({
            id,
            name: `${data.personalDetails?.firstName || ''} ${
              data.personalDetails?.lastName || ''
            }`.trim(),
            ...data,
          }),
        );

        // Filter out students with empty names
        const validStudents = studentsArray.filter(
          (student) => student.name && student.name.trim() !== '',
        );
        setAllStudents(validStudents);
      } catch (error) {
        console.error('Error fetching initial data:', error);
      }
    };

    if (isOpen) {
      fetchInitialData();
    }
  }, [isOpen]);

  // Filter students based on search
  const filteredStudents = allStudents.filter((student) =>
    student.name.toLowerCase().includes(studentSearch.toLowerCase()),
  );

  // Fetch batches when course is selected
  useEffect(() => {
    const fetchBatches = async () => {
      if (!formData.courseId) {
        setBatches([]);
        return;
      }

      try {
        const batchesSnapshot = await get(
          ref(db, `courses/${formData.courseId}/batches`),
        );
        const batchesData = batchesSnapshot.val() || {};

        setBatches(
          Object.entries(batchesData).map(([id, data]) => ({
            id,
            batchId: data.batchId,
            branch: data.branch,
            ...data,
          })),
        );
      } catch (error) {
        console.error('Error fetching batches:', error);
      }
    };

    fetchBatches();
  }, [formData.courseId]);

  // Handle student selection
  const handleStudentSelect = (student) => {
    const isSelected = selectedStudents.some((s) => s.id === student.id);

    if (isSelected) {
      setSelectedStudents(selectedStudents.filter((s) => s.id !== student.id));
      setFormData({
        ...formData,
        selectedStudents: formData.selectedStudents.filter(
          (id) => id !== student.id,
        ),
      });
    } else {
      setSelectedStudents([...selectedStudents, student]);
      setFormData({
        ...formData,
        selectedStudents: [...formData.selectedStudents, student.id],
      });
    }
    setStudentSearch(''); // Clear search after selection
  };

  // Update handleSubmit to handle multiple students
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Create payment requests for each selected student
      const requests = formData.selectedStudents.map((studentId) => {
        const student = allStudents.find((s) => s.id === studentId);
        return {
          courseId: formData.courseId,
          batchId: formData.batchId,
          studentId,
          studentName: student.name,
          amount: parseFloat(formData.amount),
          paymentType: formData.paymentType,
          installmentNumber:
            formData.paymentType === 'installment'
              ? parseInt(formData.installmentNumber)
              : null,
          totalInstallments:
            formData.paymentType === 'installment'
              ? parseInt(formData.totalInstallments)
              : null,
          dueDate: formData.dueDate,
          notes: formData.notes,
          courseName: courses.find((c) => c.id === formData.courseId)?.name,
          batchName: batches.find((b) => b.id === formData.batchId)?.batchId,
          createdAt: new Date().toISOString(),
          status: 'pending',
        };
      });

      const paymentRequestRef = ref(db, 'paymentRequests');
      await Promise.all(
        requests.map((request) => push(paymentRequestRef, request)),
      );

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error creating payment requests:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center">
      <div className="w-full md:w-[800px] m-4">
        <div className="bg-white rounded-lg shadow-xl max-h-[calc(100vh-2rem)] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b px-4 py-3 flex justify-between items-center">
            <h3 className="text-lg md:text-xl font-medium text-gray-900">
              Create Payment Request
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-4 md:p-6">
            <div className="md:grid md:grid-cols-2 md:gap-6">
              {/* Left Column */}
              <div className="space-y-4">
                {/* Course Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Course
                  </label>
                  <select
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                    value={formData.courseId}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        courseId: e.target.value,
                        batchId: '',
                        selectedStudents: [],
                        paymentId: '',
                      })
                    }
                    required
                  >
                    <option value="">Select Course</option>
                    {courses.map((course) => (
                      <option key={course.id} value={course.id}>
                        {course.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Batch Selection */}
                {formData.courseId && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Batch
                    </label>
                    <select
                      className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                      value={formData.batchId}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          batchId: e.target.value,
                          selectedStudents: [],
                          paymentId: '',
                        })
                      }
                      required
                    >
                      <option value="">Select Batch</option>
                      {batches.map((batch) => {
                        const resolved = resolveBranchRef(batch, {
                          byId: branchesById,
                          bySlug: branchesBySlug,
                        });
                        return (
                          <option key={batch.id} value={batch.id}>
                            {batch.batchId}
                            {resolved?.name ? ` - ${resolved.name}` : ''}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                )}

                {/* Student Search and Selection */}
                {formData.batchId && (
                  <div className="space-y-4">
                    <div className="relative">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Search and Select Students
                      </label>
                      <input
                        type="text"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                        placeholder="Type student name..."
                        value={studentSearch}
                        onChange={(e) => setStudentSearch(e.target.value)}
                      />
                      {/* Predictive Search Results */}
                      {studentSearch && (
                        <div className="absolute z-10 w-full mt-1 bg-white shadow-lg max-h-60 rounded-md py-1 text-base overflow-auto focus:outline-none sm:text-sm">
                          {filteredStudents.length > 0 ? (
                            filteredStudents.map((student) => (
                              <div
                                key={student.id}
                                className={`cursor-pointer px-4 py-2 flex items-center justify-between ${
                                  selectedStudents.some(
                                    (s) => s.id === student.id,
                                  )
                                    ? 'bg-pink-50'
                                    : 'hover:bg-gray-50'
                                }`}
                                onClick={() => handleStudentSelect(student)}
                              >
                                <span>{student.name}</span>
                                {selectedStudents.some(
                                  (s) => s.id === student.id,
                                ) && <span className="text-pink-700">✓</span>}
                              </div>
                            ))
                          ) : (
                            <div className="px-4 py-2 text-gray-500">
                              No students found
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Selected Students List */}
                    {selectedStudents.length > 0 && (
                      <div className="rounded-md bg-pink-50 p-3">
                        <div className="flex justify-between items-center mb-2">
                          <h4 className="text-sm font-medium text-pink-950">
                            Selected Students ({selectedStudents.length})
                          </h4>
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedStudents([]);
                              setFormData({
                                ...formData,
                                selectedStudents: [],
                              });
                            }}
                            className="text-xs text-pink-700 hover:text-pink-900"
                          >
                            Clear All
                          </button>
                        </div>
                        <div className="space-y-1">
                          {selectedStudents.map((student) => (
                            <div
                              key={student.id}
                              className="flex justify-between items-center py-1 text-sm text-pink-900"
                            >
                              <span>{student.name}</span>
                              <button
                                type="button"
                                onClick={() => handleStudentSelect(student)}
                                className="text-pink-700 hover:text-pink-900"
                              >
                                ×
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="mt-6 md:mt-0 space-y-4">
                {selectedStudents.length > 0 && (
                  <>
                    {/* Payment Type Selection */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Payment Type
                      </label>
                      <select
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                        value={formData.paymentType}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            paymentType: e.target.value,
                            installmentNumber: '',
                            totalInstallments: '',
                          })
                        }
                        required
                      >
                        <option value="">Select Payment Type</option>
                        <option value="registration">Registration Fee</option>
                        <option value="installment">
                          Course Fee Installment
                        </option>
                      </select>
                    </div>

                    {/* Amount */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Amount (Rs.)
                      </label>
                      <input
                        type="number"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                        value={formData.amount}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            amount: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    {/* Installment Details in a row */}
                    {formData.paymentType === 'installment' && (
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Installment Number
                          </label>
                          <input
                            type="number"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                            value={formData.installmentNumber}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                installmentNumber: e.target.value,
                              })
                            }
                            min="1"
                            required
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Total Installments
                          </label>
                          <input
                            type="number"
                            className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                            value={formData.totalInstallments}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                totalInstallments: e.target.value,
                              })
                            }
                            min="1"
                            required
                          />
                        </div>
                      </div>
                    )}

                    {/* Due Date */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Due Date
                      </label>
                      <input
                        type="date"
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                        value={formData.dueDate}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            dueDate: e.target.value,
                          })
                        }
                        required
                      />
                    </div>

                    {/* Notes/Description */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Notes
                      </label>
                      <textarea
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-pink-500 focus:ring-pink-500"
                        value={formData.notes}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            notes: e.target.value,
                          })
                        }
                        rows="3"
                        placeholder="Add any additional notes or description..."
                      />
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Footer Actions */}
            <div className="mt-6 pt-4 border-t sticky bottom-0 bg-white">
              <div className="flex flex-col-reverse sm:flex-row justify-end space-y-reverse space-y-3 sm:space-y-0 sm:space-x-3">
                <button
                  type="button"
                  onClick={onClose}
                  className="w-full sm:w-auto px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !selectedStudents.length}
                  className="w-full rounded-md bg-pink-600 px-4 py-2 text-sm font-medium text-white hover:bg-pink-700 disabled:opacity-50 sm:w-auto"
                >
                  {loading ? <Spinner size="sm" /> : 'Create Request'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreatePaymentRequestModal;
